import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowDownUp, X, ExternalLink, AlertTriangle, 
  Zap, Copy, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================
// LIVE TOKEN CONFIGURATION
// ============================================================
const PLATFORM_FEE_BPS = 100; // 100 basis points = 1%

// Token addresses
const SOL_MINT = 'So11111111111111111111111111111111111111112';
// LIVE $SLAP Token on Pump.fun
const SLAP_TOKEN_MINT = 'A7rKF3srg28UArWp1ibVVUbQmnjUnitTtq7ZFR4tpump';

const JupiterSwapWidget = ({ isOpen, onClose, onSwapComplete }) => {
  const [copied, setCopied] = useState(false);

  // Get local wallet info
  const burnerWallet = localStorage.getItem('burner_wallet');
  const walletData = burnerWallet ? JSON.parse(burnerWallet) : null;
  const playerId = walletData?.playerId || localStorage.getItem('player_id') || 'Not created';

  // Open Jupiter directly
  const openJupiterExternal = () => {
    const jupiterUrl = `https://jup.ag/swap/SOL-${SLAP_TOKEN_MINT}`;
    window.open(jupiterUrl, '_blank');
    toast.success('Jupiter opened!', {
      description: 'Swap SOL → $SLAP in new tab',
    });
  };

  // Copy player ID
  const handleCopy = () => {
    navigator.clipboard.writeText(playerId);
    setCopied(true);
    toast.success('Player ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="jupiter-swap-modal"
      >
        <motion.div
          className="bg-[#0d0d0d] border-2 border-purple-500/50 rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col relative"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* FIX: RED X CLOSE BUTTON - Top Right Absolute */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors z-20"
            data-testid="close-swap-modal-x"
          >
            <X className="w-6 h-6 text-red-400" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-white/10 shrink-0 pr-14">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <ArrowDownUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-lg">SWAP</h3>
              <p className="text-white/50 text-xs">SOL → $SLAP</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 overflow-y-auto flex-1">
            {/* Fee Notice */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
              <div className="flex items-center gap-2 text-yellow-400 text-sm font-bold mb-1">
                <AlertTriangle className="w-4 h-4" />
                Platform Fee: {PLATFORM_FEE_BPS / 100}%
              </div>
              <p className="text-yellow-200/70 text-xs">
                A {PLATFORM_FEE_BPS / 100}% fee supports platform development.
              </p>
            </div>

            {/* Live Token Badge */}
            <div className="flex items-center justify-center gap-2 py-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-green-400 text-xs font-bold uppercase tracking-wider">
                Live Token Active
              </span>
            </div>

            {/* Token Preview */}
            <div className="bg-black/30 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-xl">
                    ◎
                  </div>
                  <div>
                    <div className="text-white font-bold">SOL</div>
                    <div className="text-white/50 text-xs">Solana</div>
                  </div>
                </div>
                <ArrowDownUp className="w-6 h-6 text-white/30" />
                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-white font-bold text-right">$SLAP</div>
                    <div className="text-white/50 text-xs text-right">Game Token</div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-xl">
                    🐸
                  </div>
                </div>
              </div>
            </div>

            {/* Your Player ID */}
            <div className="bg-black/50 rounded-xl p-3 border border-white/10">
              <p className="text-white/50 text-xs mb-1">Your Player ID</p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-yellow-400 font-mono text-sm break-all flex-1">
                  {playerId}
                </code>
                <button
                  onClick={handleCopy}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors shrink-0"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-white/60" />
                  )}
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
              <h4 className="text-purple-400 font-bold mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                How to Swap
              </h4>
              <ol className="text-white/70 text-sm space-y-2 list-decimal list-inside">
                <li>Click the button below to open Jupiter</li>
                <li>Connect your external Solana wallet</li>
                <li>Swap SOL for $SLAP tokens</li>
                <li>Send $SLAP to your player wallet address</li>
              </ol>
            </div>

            {/* Open Jupiter Button */}
            <button
              onClick={openJupiterExternal}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-bold text-white hover:from-purple-500 hover:to-blue-500 transition-all flex items-center justify-center gap-2"
              data-testid="open-jupiter-btn"
            >
              <ExternalLink className="w-5 h-5" />
              OPEN JUPITER SWAP
            </button>

            {/* Token Contract */}
            <div className="text-center">
              <p className="text-white/40 text-xs mb-1">$SLAP Contract Address:</p>
              <code className="text-purple-400 text-xs break-all">
                {SLAP_TOKEN_MINT}
              </code>
            </div>
          </div>

          {/* FIX: BOTTOM CLOSE BUTTON */}
          <div className="p-4 border-t border-white/10 shrink-0">
            <button
              onClick={onClose}
              className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-xl font-bold text-red-400 transition-colors flex items-center justify-center gap-2"
              data-testid="close-swap-modal-bottom"
            >
              <X className="w-5 h-5" />
              CLOSE
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default JupiterSwapWidget;
